import { useDispatch, useSelector } from "react-redux"

import { Chat, MessageStatus, WebsocketActions, WebsocketChatMessage } from "../interfaces"

import { createChatWithNewMessage } from "./api"

import { chatSelector } from "@utils/store/selectors/chat"
import { AppDispatch } from "@utils/store/store"
import { addMessages, updateChat, updateMessage } from "@utils/store/slices"

const getActualDate = (timestamp: string | undefined) => timestamp || new Date().toISOString()

export function useChatSocketHandlers() {
    const { activeChatId, chats, role } = useSelector(chatSelector)
    const dispatch = useDispatch<AppDispatch>()

    const updateChatAfterMessage = (data: WebsocketChatMessage, viewed: boolean) => {
        const chatIndex = chats.findIndex((chat) => chat.chatId === activeChatId)
        if (chatIndex === -1) return

        const updatedChat: Partial<Chat> = {
            chatId: chats[chatIndex].chatId,
            lastMessage: {
                id: data.id,
                timestamp: getActualDate(data.timestamp),
                viewed,
            },
        }

        dispatch(updateChat(updatedChat))
    }

    const handleCreatedMessage = (data: WebsocketChatMessage) => {
        dispatch(
            updateMessage({
                hash: data.hash,
                id: data.id,
                timestamp: getActualDate(data.timestamp),
                status: MessageStatus.CREATED,
            })
        )
        updateChatAfterMessage(data, true)
    }

    const handleViewedMessage = (data: WebsocketChatMessage) => {
        dispatch(
            updateMessage({
                id: data.id,
                chatId: data.chatId,
                status: MessageStatus.VIEWED,
            })
        )
    }

    const handleNewMessageInClosedChat = (data: WebsocketChatMessage) => {
        const chat = chats.find((chat) => chat.chatId === data.chatId)

        if (!chat) {
            if (data.chatId && role) {
                void dispatch(createChatWithNewMessage(data.chatId, role))
            }
            return
        }

        const updatedChat: Partial<Chat> = {
            ...chat,
            unreadCount: chat.unreadCount + 1,
            lastMessage: {
                id: data.id,
                timestamp: getActualDate(data.timestamp),
                viewed: false,
            },
        }
        dispatch(updateChat(updatedChat))
    }

    const handleNewMessage = (data: WebsocketChatMessage) => {
        const hasText = Boolean(data.text?.trim())
        const hasAttachments = Boolean(data.attachmentIds?.length)
        if ((!hasText && !hasAttachments) || !data.timestamp || !data.externalUserId) return

        if (data.chatId === activeChatId) {
            dispatch(
                addMessages([
                    {
                        id: data.id,
                        chatId: data.chatId,
                        externalUserId: data.externalUserId,
                        timestamp: data.timestamp,
                        text: data.text ?? "",
                        attachmentIds: data.attachmentIds,
                        attachments: data.attachments,
                        status: MessageStatus.CREATED,
                    },
                ])
            )
            updateChatAfterMessage(data, false)
        } else {
            handleNewMessageInClosedChat(data)
        }
    }

    const handleSocketMessage = (data: WebsocketChatMessage) => {
        switch (data.action) {
            case WebsocketActions.CREATED:
                handleCreatedMessage(data)
                break
            case WebsocketActions.VIEWED:
                handleViewedMessage(data)
                break
            case WebsocketActions.NEW:
                handleNewMessage(data)
                break
            default:
                console.log("Неизвестное действие:", data.action)
        }
    }

    return { handleSocketMessage }
}
